import React from 'react';
import { Button } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import PersonIcon from '@mui/icons-material/Person';
import { useThemeApp } from 'components/Theme/theme';
import CircularProgress from '@mui/material/CircularProgress';

interface ImageColumnProps {
    altText: string;
    onClick?: () => void;
    imageUrl?: string | null;
    type?: 'avatar' | 'normal';
}

const ImageColumn: React.FC<ImageColumnProps> = ({
    imageUrl,
    altText,
    onClick,
    type = 'normal',
}) => {
    const { colors } = useThemeApp();
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (imageUrl) {
            createThumbnail(imageUrl);
        } else {
            setLoading(false);
        }
    }, [imageUrl]);

    const createThumbnail = (url: string) => {
        setLoading(true);
        const img = new Image();

        img.onload = () => {
            setLoading(false);
        };

        img.onerror = (err) => {
            console.warn('Erro ao carregar imagem, mas tentando mesmo assim:', url);
            setLoading(false);
        };

        img.src = url;
    };

    const getContainerStyle = () => {
        if (type === 'avatar') {
            return {
                padding: 0,
                height: '60px',
                width: '60px',
                minWidth: 'unset',
                overflow: 'hidden',
                borderRadius: '50%',
            };
        }
        return {
            padding: 0,
            height: '60px',
            width: '100px',
            minWidth: 'unset',
            overflow: 'hidden',
            borderRadius: '6px',
        };
    };

    const getImageStyle = () => {
        const baseStyle = {
            width: '100%',
            height: '100%',
            objectFit: 'cover' as const,
            transform: 'translateZ(0)',
            imageRendering: '-webkit-optimize-contrast' as const,
        };

        if (type === 'avatar') {
            return {
                ...baseStyle,
                borderRadius: '50%',
            };
        }
        return {
            ...baseStyle,
            borderRadius: '6px',
        };
    };

    const getPlaceholderStyle = () => {
        const baseStyle = {
            width: '100%',
            height: '100%',
            display: 'flex',
            backgroundColor: colors.card,
            alignItems: 'center' as const,
            justifyContent: 'center' as const,
        };

        if (type === 'avatar') {
            return {
                ...baseStyle,
                borderRadius: '50%',
            };
        }
        return {
            ...baseStyle,
            borderRadius: '6px',
        };
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div style={getPlaceholderStyle()}>
                    <CircularProgress style={{ color: colors.text }} size={24} />
                </div>
            );
        }

        if (imageUrl) {
            return (
                <img
                    alt={altText}
                    src={imageUrl}
                    style={getImageStyle()}
                    onError={(e) => {
                        console.warn('Erro no img tag, mas mantendo ícone');
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement?.childNodes.forEach(node => {
                            if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'DIV') {
                                (node as HTMLElement).style.display = 'flex';
                            }
                        });
                    }}
                />
            );
        }

        const iconStyle = {
            fontSize: type === 'avatar' ? 30 : 24,
            color: colors.text
        };

        return (
            <div style={getPlaceholderStyle()}>
                {type === 'avatar' ? (
                    <PersonIcon style={iconStyle} />
                ) : (
                    <ImageIcon style={iconStyle} />
                )}
            </div>
        );
    };

    const handleClick = () => {
        if (onClick) {
            onClick();
        }

        else if (imageUrl) {
            window.open(imageUrl, '_blank');
        }
    };

    return (
        <>
            <Button
                disabled={!imageUrl && !onClick || loading}
                style={getContainerStyle()}
                onClick={handleClick}
            >
                {renderContent()}
            </Button>
        </>
    );
};

export default React.memo(ImageColumn);